import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Col,
  Dropdown,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import {
  Archive,
  ArrowLeft,
  CheckCircle,
  Copy,
  Edit,
  Eye,
  MessageSquare,
  Printer,
  Send,
  ShoppingCart,
  XCircle,
} from 'lucide-react';
import StatusBadge from '../../components/quote/StatusBadge';
import QuoteGrid from '../../components/quote/QuoteGrid';
import { formatMoney } from '../../components/quote/TotalsPanel';
import { api } from '../../utils/apiClient';
import { showToast } from '../../utils/toast';
import { ROUTE, withQuery } from '../../utils/navigation';
import { isQuoteChatUiEnabled } from '../../utils/featureFlags';
import { calcQuoteTotals } from './quoteUtils';
import './quote-detail.css';

const SALES_REP_LABEL_KEYS = {
  admin: 'quoteForm.salesReps.admin',
  rep1: 'quoteForm.salesReps.rep1',
};

function formatDate(value, withTime = false, locale = undefined) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return withTime ? d.toLocaleString(locale) : d.toLocaleDateString(locale);
}

function formatAddress(address) {
  if (!address || typeof address !== 'object') return null;
  const parts = [address.street, address.city, address.state, address.zip, address.country]
    .map((p) => (p == null ? '' : String(p).trim()))
    .filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

function MetaRow({ label, children }) {
  return (
    <div className="q2c-qd-meta-row">
      <span className="q2c-qd-meta-label">{label}</span>
      <span className="q2c-qd-meta-value">{children}</span>
    </div>
  );
}

function SectionCard({ title, action, children, className = '' }) {
  return (
    <section className={`q2c-qd-card ${className}`.trim()}>
      {(title || action) && (
        <div className="q2c-qd-card-head">
          {title ? <h3 className="q2c-qd-card-title">{title}</h3> : <span />}
          {action || null}
        </div>
      )}
      <div className="q2c-qd-card-body">{children}</div>
    </section>
  );
}

const QuoteDetailPage = ({ quoteId, onNavigate, storeCurrency = 'USD' }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [quote, setQuote] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [activityExpanded, setActivityExpanded] = useState(false);
  const [chatStarting, setChatStarting] = useState(false);

  const startChat = async () => {
    if (chatStarting || !quoteId) return;
    setChatStarting(true);
    try {
      const data = await api.createChatThread(quoteId);
      const threadId = data?.thread?.id;
      if (!threadId) throw new Error(t('quoteDetail.toast.chatNotCreated'));
      onNavigate(withQuery(ROUTE.CHAT, { thread: threadId }));
    } catch (err) {
      showToast(err.message || t('quoteDetail.toast.startChatFailed'), 'error');
    } finally {
      setChatStarting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [quoteData, historyData] = await Promise.all([
        api.getQuote(quoteId),
        api.quoteHistory(quoteId),
      ]);
      setQuote(quoteData?.quote || null);
      setHistory(historyData?.history || []);
    } catch (err) {
      showToast(err.message || t('quoteDetail.toast.loadFailed'), 'error');
      setQuote(null);
    } finally {
      setLoading(false);
    }
  }, [quoteId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const send = async () => {
    setActing(true);
    try {
      await api.sendQuote(quoteId);
      showToast(t('quoteDetail.toast.quoteSent'), 'success');
      load();
    } catch (err) {
      showToast(err.message || t('quoteDetail.toast.sendFailed'), 'error');
    } finally {
      setActing(false);
    }
  };

  const complete = async () => {
    setActing(true);
    try {
      await api.completeQuote(quoteId);
      showToast(t('quoteDetail.toast.quoteCompleted'), 'success');
      load();
    } catch (err) {
      showToast(err.message || t('quoteDetail.toast.completeFailed'), 'error');
    } finally {
      setActing(false);
    }
  };

  const previewQuote = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const data = await api.getQuoteDocument(quoteId);
      setPreviewHtml(data?.html || `<p>${t('quoteDetail.previewUnavailable')}</p>`);
    } catch (err) {
      setPreviewHtml('');
      setPreviewOpen(false);
      showToast(err.message || t('quoteDetail.toast.previewFailed'), 'error');
    } finally {
      setPreviewLoading(false);
    }
  };

  const printQuote = async () => {
    setActing(true);
    try {
      const data = await api.getQuoteDocument(quoteId, 'pdf');
      if (!data?.pdfBase64) {
        throw new Error(t('quoteDetail.toast.pdfNotAvailable'));
      }
      const binary = atob(data.pdfBase64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || 'quote.pdf';
        a.click();
      } else {
        win.addEventListener('load', () => {
          try {
            win.focus();
            win.print();
          } catch {
            /* ignore print blockers */
          }
        });
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      showToast(t('quoteDetail.toast.pdfOpened'), 'success');
    } catch (err) {
      showToast(err.message || t('quoteDetail.toast.printFailed'), 'error');
    } finally {
      setActing(false);
    }
  };

  const createOrder = () => {
    if (!quote?.cartLink) {
      showToast(t('quoteDetail.toast.sendFirstForCart'), 'warning');
      return;
    }
    window.open(quote.cartLink, '_blank', 'noopener,noreferrer');
  };

  const runConfirmAction = async () => {
    if (!confirmAction) return;
    setActing(true);
    try {
      if (confirmAction === 'archive') {
        await api.archiveQuote(quoteId);
        showToast(t('quoteDetail.toast.quoteArchived'), 'success');
      } else if (confirmAction === 'cancel') {
        await api.cancelQuote(quoteId);
        showToast(t('quoteDetail.toast.quoteCancelled'), 'success');
      }
      setConfirmAction(null);
      load();
    } catch (err) {
      showToast(err.message || t('quoteDetail.toast.actionFailed'), 'error');
    } finally {
      setActing(false);
    }
  };

  const copyText = async (text, label) => {
    if (!text) {
      showToast(t('quoteDetail.toast.noToCopy', { label }), 'error');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      showToast(t('quoteDetail.toast.labelCopied', { label }), 'success');
    } catch {
      showToast(t('quoteDetail.toast.couldNotCopy', { label }), 'error');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!quote) {
    return <div className="alert alert-warning m-3">{t('quoteDetail.toast.quoteNotFound')}</div>;
  }

  const isTerminal =
    quote.status === 'Completed' ||
    quote.status === 'Archived' ||
    quote.status === 'Cancelled';
  const canSend = !isTerminal;
  const canComplete = !isTerminal;
  const canArchive = quote.status !== 'Archived' && quote.status !== 'Cancelled';
  const canCancel = quote.status !== 'Cancelled' && quote.status !== 'Completed';
  const customer = quote.customer || {};
  const address = formatAddress(customer.address);
  const currency = quote.currency || storeCurrency || 'USD';
  const totals = calcQuoteTotals(
    quote.items || [],
    Number(quote.bulkDiscountPct) || 0,
    Number(quote.shipping) || 0,
    Number(quote.shippingAdditional) || 0,
  );
  const itemCount = (quote.items || []).reduce((sum, item) => sum + (Number(item.qty) || 0), 0);
  const salesRepKey = SALES_REP_LABEL_KEYS[quote.salesRepId];
  const salesRep = salesRepKey ? t(salesRepKey) : quote.salesRepId || '—';
  const notes = quote.notes || {};
  const hasNotes = Boolean(notes.customer || notes.messageToCustomer || notes.staff);

  return (
    <div className="q2c-qd">
      <button
        type="button"
        className="q2c-qd-back"
        onClick={() => onNavigate(ROUTE.QUOTES)}
      >
        <ArrowLeft size={16} />
        {t('quoteDetail.backToQuotes')}
      </button>

      <header className="q2c-qd-header">
        <div className="q2c-qd-header-main">
          <div className="q2c-qd-title-row">
            <h1 className="q2c-qd-number">{quote.number}</h1>
            <StatusBadge status={quote.status} />
          </div>
          <p className="q2c-qd-subtitle">
            <span className="fw-semibold text-dark">{customer.name || t('quoteDetail.unknownCustomer')}</span>
            {customer.email ? <span> · {customer.email}</span> : null}
            {customer.company ? <span> · {customer.company}</span> : null}
          </p>
          <p className="q2c-qd-meta-line">
            {t('quoteDetail.created', { date: formatDate(quote.createdAt, true, locale) })}
            {quote.sentAt ? ` · ${t('quoteDetail.sent', { date: formatDate(quote.sentAt, true, locale) })}` : ''}
            {quote.openedAt ? ` · ${t('quoteDetail.opened', { date: formatDate(quote.openedAt, true, locale) })}` : ''}
            {quote.completedAt ? ` · ${t('quoteDetail.completed', { date: formatDate(quote.completedAt, true, locale) })}` : ''}
          </p>
        </div>

        <div className="q2c-qd-actions">
          <Button
            variant="primary"
            className="d-flex align-items-center gap-1"
            onClick={send}
            disabled={acting || !canSend}
          >
            <Send size={16} />
            {t('quoteDetail.sendQuote')}
          </Button>
          <Button
            variant="warning"
            className="d-flex align-items-center gap-1 text-dark"
            onClick={() => onNavigate(`quotes/${quoteId}/edit`)}
            disabled={isTerminal}
          >
            <Edit size={16} />
            {t('quoteDetail.editQuote')}
          </Button>
          {isQuoteChatUiEnabled() ? (
            <Button
              variant="outline-danger"
              className="d-flex align-items-center gap-1"
              onClick={startChat}
              disabled={chatStarting}
            >
              <MessageSquare size={16} />
              {chatStarting ? t('quoteDetail.opening') : t('quoteDetail.liveChat')}
            </Button>
          ) : null}
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="outline-secondary"
              className="q2c-qd-more-btn d-flex align-items-center gap-1"
              id="quote-more-actions"
            >
              {t('quoteDetail.moreActions')}
            </Dropdown.Toggle>
            <Dropdown.Menu className="q2c-qd-more-menu">
              <Dropdown.Header className="q2c-qd-more-section">{t('quoteDetail.quoteActions')}</Dropdown.Header>
              <Dropdown.Item
                className="q2c-qd-more-item"
                onClick={previewQuote}
                disabled={acting || previewLoading}
              >
                <Eye size={16} className="q2c-qd-more-icon" />
                <span>
                  <span className="q2c-qd-more-title">{t('quoteDetail.previewQuote')}</span>
                  <span className="q2c-qd-more-desc">{t('quoteDetail.previewQuoteDesc')}</span>
                </span>
              </Dropdown.Item>
              <Dropdown.Item
                className="q2c-qd-more-item"
                onClick={printQuote}
                disabled={acting}
              >
                <Printer size={16} className="q2c-qd-more-icon" />
                <span>
                  <span className="q2c-qd-more-title">{t('quoteDetail.printQuote')}</span>
                  <span className="q2c-qd-more-desc">{t('quoteDetail.printQuoteDesc')}</span>
                </span>
              </Dropdown.Item>

              <Dropdown.Header className="q2c-qd-more-section">{t('quoteDetail.customerActions')}</Dropdown.Header>
              <Dropdown.Item
                className="q2c-qd-more-item"
                onClick={createOrder}
                disabled={acting}
              >
                <ShoppingCart size={16} className="q2c-qd-more-icon" />
                <span>
                  <span className="q2c-qd-more-title">{t('quoteDetail.createOrder')}</span>
                  <span className="q2c-qd-more-desc">{t('quoteDetail.createOrderDesc')}</span>
                </span>
              </Dropdown.Item>

              <Dropdown.Header className="q2c-qd-more-section">{t('quoteDetail.management')}</Dropdown.Header>
              {canComplete ? (
                <Dropdown.Item
                  className="q2c-qd-more-item"
                  onClick={complete}
                  disabled={acting}
                >
                  <CheckCircle size={16} className="q2c-qd-more-icon" />
                  <span>
                    <span className="q2c-qd-more-title">{t('quoteDetail.markCompleted')}</span>
                    <span className="q2c-qd-more-desc">{t('quoteDetail.markCompletedDesc')}</span>
                  </span>
                </Dropdown.Item>
              ) : null}
              {canArchive ? (
                <Dropdown.Item
                  className="q2c-qd-more-item"
                  onClick={() => setConfirmAction('archive')}
                  disabled={acting}
                >
                  <Archive size={16} className="q2c-qd-more-icon" />
                  <span>
                    <span className="q2c-qd-more-title">{t('quoteDetail.archiveQuote')}</span>
                    <span className="q2c-qd-more-desc">{t('quoteDetail.archiveQuoteDesc')}</span>
                  </span>
                </Dropdown.Item>
              ) : null}
              {canCancel ? (
                <Dropdown.Item
                  className="q2c-qd-more-item q2c-qd-more-item-danger"
                  onClick={() => setConfirmAction('cancel')}
                  disabled={acting}
                >
                  <XCircle size={16} className="q2c-qd-more-icon" />
                  <span>
                    <span className="q2c-qd-more-title">{t('quoteDetail.cancelQuote')}</span>
                    <span className="q2c-qd-more-desc">{t('quoteDetail.cancelQuoteDesc')}</span>
                  </span>
                </Dropdown.Item>
              ) : null}

              <Dropdown.Divider />
              <Dropdown.Item onClick={() => copyText(quote.number, t('quoteDetail.quoteNumberCopyLabel'))}>
                <Copy size={14} className="me-2" />
                {t('quoteDetail.copyQuoteNumber')}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </header>

      <Row className="g-3">
        <Col lg={8} className="d-flex flex-column gap-3">
          <SectionCard title={t('quoteDetail.customerDetails')}>
            <div className="q2c-qd-customer-grid">
              <div>
                <div className="q2c-qd-field-label">{t('quoteDetail.name')}</div>
                <div className="q2c-qd-field-value">{customer.name || '—'}</div>
              </div>
              <div>
                <div className="q2c-qd-field-label">{t('quoteDetail.email')}</div>
                <div className="q2c-qd-field-value">
                  {customer.email ? (
                    <a href={`mailto:${customer.email}`}>{customer.email}</a>
                  ) : (
                    '—'
                  )}
                </div>
              </div>
              <div>
                <div className="q2c-qd-field-label">{t('quoteDetail.phone')}</div>
                <div className="q2c-qd-field-value">{customer.phone || '—'}</div>
              </div>
              <div>
                <div className="q2c-qd-field-label">{t('quoteDetail.company')}</div>
                <div className="q2c-qd-field-value">{customer.company || '—'}</div>
              </div>
              <div>
                <div className="q2c-qd-field-label">{t('quoteDetail.customerGroup')}</div>
                <div className="q2c-qd-field-value">
                  {customer.customerGroupId || t('quoteDetail.default')}
                </div>
              </div>
              <div className="q2c-qd-customer-address">
                <div className="q2c-qd-field-label">{t('quoteDetail.billingAddress')}</div>
                <div className="q2c-qd-field-value">{address || t('quoteDetail.noAddress')}</div>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title={t('quoteDetail.products')}
            action={
              <span className="q2c-qd-chip">
                {t('quoteDetail.itemsCount', { count: itemCount })}
              </span>
            }
          >
            <QuoteGrid items={quote.items || []} currency={currency} />
          </SectionCard>

          {hasNotes ? (
            <SectionCard title={t('quoteDetail.notes')}>
              <div className="q2c-qd-notes">
                {notes.messageToCustomer ? (
                  <div className="q2c-qd-note-block">
                    <div className="q2c-qd-field-label">{t('quoteDetail.messageToCustomer')}</div>
                    <div
                      className="q2c-qd-note-html"
                      dangerouslySetInnerHTML={{ __html: notes.messageToCustomer }}
                    />
                  </div>
                ) : null}
                {notes.customer ? (
                  <div className="q2c-qd-note-block">
                    <div className="q2c-qd-field-label">{t('quoteDetail.customerNotes')}</div>
                    <div className="q2c-qd-field-value">{notes.customer}</div>
                  </div>
                ) : null}
                {notes.staff ? (
                  <div className="q2c-qd-note-block">
                    <div className="q2c-qd-field-label">{t('quoteDetail.staffNotes')}</div>
                    <div className="q2c-qd-field-value">{notes.staff}</div>
                  </div>
                ) : null}
              </div>
            </SectionCard>
          ) : null}

          <SectionCard title={t('quoteDetail.activity')}>
            {history.length === 0 ? (
              <p className="q2c-qd-empty mb-0">{t('quoteDetail.noActivity')}</p>
            ) : (
              <>
                <ol className="q2c-qd-timeline">
                  {(activityExpanded ? history : history.slice(0, 4)).map((entry) => (
                    <li key={entry.id} className="q2c-qd-timeline-item">
                      <div className="q2c-qd-timeline-dot" />
                      <div className="q2c-qd-timeline-content">
                        <div className="q2c-qd-timeline-action">{entry.action}</div>
                        <div className="q2c-qd-timeline-message">{entry.message}</div>
                        <div className="q2c-qd-timeline-time">
                          {formatDate(entry.createdAt, true, locale)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
                {history.length > 4 ? (
                  <button
                    type="button"
                    className="q2c-qd-activity-expand"
                    onClick={() => setActivityExpanded((open) => !open)}
                  >
                    {activityExpanded
                      ? t('quoteDetail.showLess')
                      : t('quoteDetail.expandMore', { count: history.length - 4 })}
                  </button>
                ) : null}
              </>
            )}
          </SectionCard>
        </Col>

        <Col lg={4} className="d-flex flex-column gap-3">
          <SectionCard title={t('quoteDetail.quoteInformation')}>
            <div className="q2c-qd-meta-list">
              <MetaRow label={t('quoteDetail.status')}>
                <StatusBadge status={quote.status} />
              </MetaRow>
              <MetaRow label={t('quoteDetail.source')}>
                {quote.source === 'frontend' ? t('quoteDetail.storefront') : t('quoteDetail.admin')}
              </MetaRow>
              <MetaRow label={t('quoteDetail.quoteNumberLabel')}>{quote.number}</MetaRow>
              <MetaRow label={t('quoteDetail.salesRep')}>{salesRep}</MetaRow>
              <MetaRow label={t('quoteDetail.currency')}>{currency}</MetaRow>
              <MetaRow label={t('quoteDetail.createdLabel')}>{formatDate(quote.createdAt, true, locale)}</MetaRow>
              <MetaRow label={t('quoteDetail.expiry')}>{formatDate(quote.expiryDate, false, locale)}</MetaRow>
              <MetaRow label={t('quoteDetail.delivery')}>{quote.deliveryTime || '—'}</MetaRow>
              {quote.ecwidOrderId ? (
                <MetaRow label={t('quoteDetail.ecwidOrder')}>{quote.ecwidOrderId}</MetaRow>
              ) : null}
            </div>
          </SectionCard>

          <SectionCard title={t('quoteDetail.quoteTotals')}>
            <div className="q2c-qd-totals">
              <div className="q2c-qd-total-row">
                <span>{t('quoteDetail.subtotal')}</span>
                <strong>{formatMoney(totals.subtotal, currency)}</strong>
              </div>
              <div className="q2c-qd-total-row">
                <span>{t('quoteDetail.bulkDiscount')}</span>
                <strong>{Number(quote.bulkDiscountPct) || 0}%</strong>
              </div>
              <div className="q2c-qd-total-row">
                <span>{t('quoteDetail.discount')}</span>
                <strong>{formatMoney(totals.discountAmount, currency)}</strong>
              </div>
              <div className="q2c-qd-total-row">
                <span>{t('quoteDetail.shipping')}</span>
                <strong>
                  {formatMoney(quote.shipping, currency)}
                  {quote.shippingMethodName ? ` · ${quote.shippingMethodName}` : ''}
                </strong>
              </div>
              {Number(quote.shippingAdditional) > 0 ? (
                <div className="q2c-qd-total-row">
                  <span>{t('quoteDetail.shippingAdditional')}</span>
                  <strong>{formatMoney(quote.shippingAdditional, currency)}</strong>
                </div>
              ) : null}
              <div className="q2c-qd-total-row q2c-qd-total-final">
                <span>{t('quoteDetail.quoteTotal')}</span>
                <strong>{formatMoney(totals.total, currency)}</strong>
              </div>
            </div>
          </SectionCard>

          {isQuoteChatUiEnabled() ? (
            <SectionCard title={t('quoteDetail.customerChat')}>
              <div className="q2c-qd-chat-empty">
                <MessageSquare size={28} strokeWidth={1.5} />
                <p className="mb-1 fw-semibold">{t('quoteDetail.noConversations')}</p>
                <p className="small text-muted mb-3">
                  {t('quoteDetail.startChatDesc')}
                </p>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={startChat}
                  disabled={chatStarting}
                >
                  {chatStarting ? t('quoteDetail.opening') : t('quoteDetail.startNewChat')}
                </Button>
              </div>
            </SectionCard>
          ) : null}
        </Col>
      </Row>

      <Modal
        show={previewOpen}
        onHide={() => setPreviewOpen(false)}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('quoteDetail.previewTitle', { number: quote.number })}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {previewLoading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <iframe
              title={t('quoteDetail.quotePreviewIframe')}
              className="q2c-qd-preview-frame"
              srcDoc={previewHtml}
              sandbox=""
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPreviewOpen(false)}>
            {t('common.close')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setPreviewOpen(false);
              printQuote();
            }}
            disabled={acting || previewLoading}
          >
            <Printer size={16} className="me-1" />
            {t('quoteDetail.printPdf')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(confirmAction)} onHide={() => setConfirmAction(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {confirmAction === 'archive' ? t('quoteDetail.archiveConfirmTitle') : t('quoteDetail.cancelConfirmTitle')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmAction === 'archive'
            ? t('quoteDetail.archiveConfirmBody', { number: quote.number })
            : t('quoteDetail.cancelConfirmBody', { number: quote.number })}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setConfirmAction(null)}
            disabled={acting}
          >
            {t('quoteDetail.keepQuote')}
          </Button>
          <Button
            variant={confirmAction === 'cancel' ? 'danger' : 'primary'}
            onClick={runConfirmAction}
            disabled={acting}
          >
            {acting ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : null}
            {confirmAction === 'archive' ? t('quoteDetail.archive') : t('quoteDetail.cancelQuoteAction')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default QuoteDetailPage;
